<template>
  <div
    v-if="showTool"
    class="padel-racket__container"
    :class="{ 'padel-racket__container--wide': wide }"
  >
    <div v-if="loading" class="preselect__spinner-container">
      <span class="preselect__spinner" aria-hidden="true" />
    </div>
    <div v-else>
      <div
        v-for="(stepData, stepIndex) in stepOptions"
        v-show="step === Number(stepIndex)"
        :key="stepIndex"
      >
        <div class="padel-racket__title">{{ t(`title.${stepData.code}`) }}</div>
        <div class="padel-racket__options">
          <div
            v-for="option in adjustedStepOptions(stepData)"
            :key="option.code"
            class="padel-racket__option"
          >
            <div class="padel-racket__option-button">
              <button
                type="button"
                class="button button--white button--small button--collapsed"
                @click="setValue(stepData.code, option.code)"
              >
                {{ t(`options.${stepData.code}.${option.code}`) }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, onMounted, reactive, ref } from 'vue';

import { generateUrl } from '@shop/utils/Routing';
import { useTranslation } from '@shop/utils/Translation';

import type {
  PadelRacketStepData,
  PadelRacketStepOption,
  PadelRacketToolBlockProps,
} from './PadelRacketToolBlock.types';

const CODE_INITIAL = 'initial';
const CODE_GENDER = 'geschikt_voor';
const CODE_LEVEL = 'niveau_spelerpadel';
const CODE_TYPE = 'type_speler_padel';
const START = 'start';
const GENDER_WOMAN = 'woman';
const GENDER_MEN = 'men';
const GENDER_KIDS = 'kids';
const LEVEL_BEGINNER = 'beginner';
const TYPE_ALLROUND = 'allround';

const props = defineProps<PadelRacketToolBlockProps>();

const { t } = useTranslation('padel_tool');

const toBool = (value: boolean | string | undefined): boolean => value === true || value === 'true';

const wide = computed(() => toBool(props.wide));

const step = ref(0);
const loading = ref(false);
const showTool = ref(true);
const selectedValues = reactive<Record<string, string>>({});

const stepOptions: Record<number, PadelRacketStepData> = {
  0: { code: CODE_INITIAL, options: [{ code: START }] },
  1: {
    code: CODE_GENDER,
    options: [{ code: GENDER_WOMAN }, { code: GENDER_MEN }, { code: GENDER_KIDS }],
  },
  2: {
    code: CODE_LEVEL,
    options: [{ code: LEVEL_BEGINNER }, { code: 'advanced' }, { code: 'expert' }],
  },
  3: {
    code: CODE_TYPE,
    options: [
      { code: 'attacking', info_hover: true },
      { code: 'defensive', info_hover: true },
      { code: TYPE_ALLROUND, info_hover: true },
    ],
  },
};

const lastStep = Object.keys(stepOptions).length - 1;

const adjustedStepOptions = (stepData: PadelRacketStepData): PadelRacketStepOption[] => {
  const gender = selectedValues[CODE_GENDER] ?? null;
  const level = selectedValues[CODE_LEVEL] ?? null;

  if (stepData.code === CODE_TYPE && (gender === GENDER_WOMAN || gender === GENDER_KIDS)) {
    return stepData.options.filter(option => option.code === TYPE_ALLROUND);
  }
  if (stepData.code === CODE_TYPE && level === LEVEL_BEGINNER && gender === GENDER_MEN) {
    return stepData.options.filter(option => option.code === TYPE_ALLROUND);
  }

  return stepData.options;
};

const setValue = (code: string, value: string): void => {
  selectedValues[code] = value;
  if (step.value === lastStep) {
    void showResults();
  } else {
    step.value += 1;
  }
};

const showResults = async (): Promise<void> => {
  loading.value = true;

  const params: Record<string, string> = {
    url_path: window.location.pathname,
    ...selectedValues,
  };

  try {
    const result = await axios.get<{ url?: string }>(
      generateUrl('category_tool_padel_racket_url', params)
    );
    if (result.data.url) {
      window.location.href = result.data.url;
    } else {
      step.value = 0;
    }
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('tool_result')) {
    showTool.value = false;
  }
});
</script>
